/**
 * Order book walks: fee estimates, market-buy-by-amount, and market-sell-by-shares (inverse walk).
 * Levels use total_shares, price, total_amount (or shares × price), and optional total_fees.
 */

import { convertFromCanonical, convertToCanonical } from '@/util/currencies'

/**
 * @param {'asc' | 'desc'} fillPriceDirection asc = asks (buy), desc = bids (sell)
 * @returns {Array<{ level: object, levelShares: number, levelTotalAmount: number, levelFees: number }>}
 */
function normalizeBookLevels(series, fillPriceDirection) {
  const sorted =
    fillPriceDirection === 'desc'
      ? [...series].sort((a, b) => (b?.price ?? 0) - (a?.price ?? 0))
      : [...series].sort((a, b) => (a?.price ?? 0) - (b?.price ?? 0))

  const levels = []
  for (const level of sorted) {
    const levelShares = level.total_shares ?? 0
    const price = level.price ?? 0
    if (levelShares <= 0 || price <= 0) continue

    const levelTotalAmount = level.total_amount ?? levelShares * price
    if (levelTotalAmount <= 0) continue

    levels.push({
      level,
      levelShares,
      levelTotalAmount,
      levelFees: level.total_fees ?? 0,
    })
  }
  return levels
}

/**
 * Compute total fee for a given number of shares by walking the order book.
 * Uses each level's total_fees and total_shares (fee for partial fill = total_fees * (shares_taken / total_shares)).
 * cumulative_fees is the fee analogue of cumulative_shares (fees at this level and all before it).
 *
 * @param {Array} series - Order book levels (any array order; see fillPriceDirection).
 *   Each level: { price, total_shares, total_fees?, cumulative_shares?, cumulative_fees? }
 *   total_fees may be undefined if API doesn't provide yet; treated as 0.
 * @param {number} requestedShares - Shares to fill (same units as series total_shares, typically canonical).
 * @param {{ fillPriceDirection?: 'asc' | 'desc' }} [options]
 *   asc = buy vs asks: lowest price first (matches {@link simulateOrderBookBuyByAmount}).
 *   desc = sell vs bids: highest price first.
 * @returns {{ totalFee: number, sharesConsumed: number }}
 */
export function calculateFeesForShares(series, requestedShares, options = {}) {
  const { fillPriceDirection = 'asc' } = options

  if (!series || series.length === 0 || requestedShares <= 0) {
    return { totalFee: 0, sharesConsumed: 0 }
  }

  const ordered =
    fillPriceDirection === 'desc'
      ? [...series].sort((a, b) => (b?.price ?? 0) - (a?.price ?? 0))
      : [...series].sort((a, b) => (a?.price ?? 0) - (b?.price ?? 0))

  let remaining = requestedShares
  let totalFee = 0

  for (let i = 0; i < ordered.length && remaining > 0; i++) {
    const level = ordered[i]
    const levelShares = level.total_shares ?? 0
    const levelFees = level.total_fees ?? 0

    if (levelShares <= 0) continue

    const sharesAtLevel = Math.min(remaining, levelShares)
    const feeAtLevel = levelFees * (sharesAtLevel / levelShares)
    totalFee += feeAtLevel
    remaining -= sharesAtLevel
  }

  const sharesConsumed = requestedShares - remaining
  return { totalFee, sharesConsumed }
}

/**
 * Shares of a limit order that fill immediately against the resting book.
 *
 * A limit buy matches asks priced at or below its limit; a limit sell matches bids priced at or
 * above it. `cumulative_shares` already aggregates every better-priced level, so the deepest
 * matchable level carries the whole matchable size regardless of how the series is sorted.
 *
 * @param {Array} series - buySeries (asks) for a buy, sellSeries (bids) for a sell.
 * @param {number} limitPrice - Limit price in the same scale as level.price (0–1).
 * @param {{ side?: 'bid' | 'ask' }} [options] bid = buy, ask = sell.
 * @returns {number} matchable shares in canonical units (0 when nothing matches).
 */
export function computeMatchableShares(series, limitPrice, options = {}) {
  const { side = 'bid' } = options
  if (!series?.length || !(limitPrice > 0)) return 0

  const exactMatch = series.find(level => Math.abs((level?.price ?? 0) - limitPrice) < 0.001)
  if (exactMatch) return exactMatch.cumulative_shares ?? 0

  return series.reduce((best, level) => {
    const price = level?.price ?? 0
    const isMatchable = side === 'ask' ? price >= limitPrice : price <= limitPrice
    return isMatchable ? Math.max(best, level.cumulative_shares ?? 0) : best
  }, 0)
}

/**
 * Market buy: spend requestedAmount (canonical) across asks, lowest price first.
 * Prefix sums on notional; find first i with cumAmount[i] >= spend, then shares = cum through i−1 plus pro-rata on i.
 *
 * @param {Array} series - buySeries / ask side (any sort order).
 * @param {number} requestedAmount - Amount to spend in canonical units.
 */
export function simulateOrderBookBuyByAmount(series, requestedAmount) {
  const empty = {
    grossSharesCanonical: 0,
    totalFeeCanonical: 0,
    amountConsumed: 0,
    remainingAmount: requestedAmount,
    lastFilledLevel: null,
    fullyFilled: false,
  }

  if (!series || series.length === 0 || requestedAmount <= 0) {
    return empty
  }

  const levels = normalizeBookLevels(series, 'asc')
  if (levels.length === 0) {
    return empty
  }

  const n = levels.length
  const cumAmount = new Array(n)
  const cumShares = new Array(n)
  const cumFees = new Array(n)

  for (let k = 0; k < n; k++) {
    const { levelShares, levelTotalAmount, levelFees } = levels[k]
    cumAmount[k] = levelTotalAmount + (k > 0 ? cumAmount[k - 1] : 0)
    cumShares[k] = levelShares + (k > 0 ? cumShares[k - 1] : 0)
    cumFees[k] = levelFees + (k > 0 ? cumFees[k - 1] : 0)
  }

  const totalBookAmount = cumAmount[n - 1]

  if (requestedAmount >= totalBookAmount) {
    const remainingAmount = requestedAmount - totalBookAmount
    return {
      grossSharesCanonical: cumShares[n - 1],
      totalFeeCanonical: cumFees[n - 1],
      amountConsumed: totalBookAmount,
      remainingAmount,
      lastFilledLevel: levels[n - 1].level,
      fullyFilled: remainingAmount <= 0,
    }
  }

  let i = 0
  while (i < n && cumAmount[i] < requestedAmount) {
    i += 1
  }

  const prevCumAmount = i > 0 ? cumAmount[i - 1] : 0
  const prevCumShares = i > 0 ? cumShares[i - 1] : 0
  const prevCumFees = i > 0 ? cumFees[i - 1] : 0
  const { level, levelShares, levelTotalAmount, levelFees } = levels[i]

  const amountIntoLevel = requestedAmount - prevCumAmount
  const shareFraction = amountIntoLevel / levelTotalAmount
  const sharesFromLevel = shareFraction * levelShares
  const feeFromLevel = levelFees * shareFraction

  return {
    grossSharesCanonical: prevCumShares + sharesFromLevel,
    totalFeeCanonical: prevCumFees + feeFromLevel,
    amountConsumed: requestedAmount,
    remainingAmount: 0,
    lastFilledLevel: level,
    fullyFilled: true,
  }
}

/**
 * Per-level fill fractions (0–1) for a limit buy walking asks lowest price first.
 * Used for admin order-book visualization of how many shares fill at each price level.
 *
 * @param {Array} series - buySeries / ask side
 * @param {number} requestedSharesCanonical
 * @param {{ maxLimitPrice?: number }} [options]
 * @returns {Record<number, number>} price → fraction of that level's total_shares filled
 */
export function computeLimitBuyFillPreview(series, requestedSharesCanonical, options = {}) {
  const { maxLimitPrice } = options
  const previewByPrice = {}

  if (!series?.length || requestedSharesCanonical <= 0) {
    return previewByPrice
  }

  const filtered =
    maxLimitPrice != null && Number.isFinite(maxLimitPrice)
      ? series.filter(level => (level?.price ?? 0) <= maxLimitPrice)
      : series

  const levels = normalizeBookLevels(filtered, 'asc')
  let remaining = requestedSharesCanonical

  for (const { level, levelShares } of levels) {
    if (remaining <= 0) break
    if (levelShares <= 0) continue

    const sharesAtLevel = Math.min(remaining, levelShares)
    const fillFraction = sharesAtLevel / levelShares
    if (fillFraction > 0 && level.price != null) {
      previewByPrice[level.price] = Math.min(1, fillFraction)
    }
    remaining -= sharesAtLevel
  }

  return previewByPrice
}

/**
 * Limit/market buy by shares: walk asks lowest price first, optionally capped by maxLimitPrice.
 * Returns the last level touched so callers can read cumulative_amount at the actual fill price.
 *
 * @param {Array} series - buySeries / ask side (any sort order).
 * @param {number} requestedSharesCanonical - Shares to buy in canonical units.
 * @param {{ maxLimitPrice?: number }} [options] - Only levels with price <= maxLimitPrice (limit order cap).
 */
export function simulateOrderBookBuyByShares(series, requestedSharesCanonical, options = {}) {
  const { maxLimitPrice } = options
  const empty = {
    grossAmountCanonical: 0,
    totalFeeCanonical: 0,
    sharesConsumed: 0,
    remainingShares: requestedSharesCanonical,
    lastFilledLevel: null,
    fullyFilled: false,
  }

  if (!series || series.length === 0 || requestedSharesCanonical <= 0) {
    return empty
  }

  const filtered =
    maxLimitPrice != null && Number.isFinite(maxLimitPrice)
      ? series.filter(level => (level?.price ?? 0) <= maxLimitPrice)
      : series

  const levels = normalizeBookLevels(filtered, 'asc')
  if (levels.length === 0) {
    return empty
  }

  const n = levels.length
  const cumAmount = new Array(n)
  const cumShares = new Array(n)
  const cumFees = new Array(n)

  for (let k = 0; k < n; k++) {
    const { levelShares, levelTotalAmount, levelFees } = levels[k]
    cumAmount[k] = levelTotalAmount + (k > 0 ? cumAmount[k - 1] : 0)
    cumShares[k] = levelShares + (k > 0 ? cumShares[k - 1] : 0)
    cumFees[k] = levelFees + (k > 0 ? cumFees[k - 1] : 0)
  }

  const totalBookShares = cumShares[n - 1]

  if (requestedSharesCanonical >= totalBookShares) {
    const remainingShares = requestedSharesCanonical - totalBookShares
    return {
      grossAmountCanonical: cumAmount[n - 1],
      totalFeeCanonical: cumFees[n - 1],
      sharesConsumed: totalBookShares,
      remainingShares,
      lastFilledLevel: levels[n - 1].level,
      fullyFilled: remainingShares <= 0,
    }
  }

  let i = 0
  while (i < n && cumShares[i] < requestedSharesCanonical) {
    i += 1
  }

  const prevCumAmount = i > 0 ? cumAmount[i - 1] : 0
  const prevCumShares = i > 0 ? cumShares[i - 1] : 0
  const prevCumFees = i > 0 ? cumFees[i - 1] : 0
  const { level, levelShares, levelTotalAmount, levelFees } = levels[i]
  const sharesIntoLevel = requestedSharesCanonical - prevCumShares
  const shareFraction = sharesIntoLevel / levelShares
  const amountFromLevel = shareFraction * levelTotalAmount
  const feeFromLevel = levelFees * shareFraction

  return {
    grossAmountCanonical: prevCumAmount + amountFromLevel,
    totalFeeCanonical: prevCumFees + feeFromLevel,
    sharesConsumed: requestedSharesCanonical,
    remainingShares: 0,
    lastFilledLevel: level,
    fullyFilled: true,
  }
}

/**
 * Market sell: place requestedShares (canonical) into bids, highest price first.
 * Inverse of {@link simulateOrderBookBuyByAmount}: prefix sums on shares; find first i with cumShares[i] >= sell size,
 * then notional = cum amount through i−1 plus pro-rata on i.
 *
 * @param {Array} series - sellSeries / bid side (any sort order).
 * @param {number} requestedSharesCanonical - Shares to sell in canonical units.
 */
export function simulateOrderBookSellByShares(series, requestedSharesCanonical) {
  const empty = {
    grossAmountCanonical: 0,
    totalFeeCanonical: 0,
    sharesConsumed: 0,
    remainingShares: requestedSharesCanonical,
    lastFilledLevel: null,
    fullyFilled: false,
  }

  if (!series || series.length === 0 || requestedSharesCanonical <= 0) {
    return empty
  }

  const levels = normalizeBookLevels(series, 'desc')
  if (levels.length === 0) {
    return empty
  }

  const n = levels.length
  const cumAmount = new Array(n)
  const cumShares = new Array(n)
  const cumFees = new Array(n)

  for (let k = 0; k < n; k++) {
    const { levelShares, levelTotalAmount, levelFees } = levels[k]
    cumAmount[k] = levelTotalAmount + (k > 0 ? cumAmount[k - 1] : 0)
    cumShares[k] = levelShares + (k > 0 ? cumShares[k - 1] : 0)
    cumFees[k] = levelFees + (k > 0 ? cumFees[k - 1] : 0)
  } 
  
  const totalBookShares = cumShares[n - 1]
  
  if (requestedSharesCanonical >= totalBookShares) {
    const remainingShares = requestedSharesCanonical - totalBookShares
    return {
      grossAmountCanonical: cumAmount[n - 1],
      totalFeeCanonical: cumFees[n - 1],
      sharesConsumed: totalBookShares,
      remainingShares,
      lastFilledLevel: levels[n - 1].level,
      fullyFilled: remainingShares <= 0,
    }
  }

  let i = 0
  while (i < n && cumShares[i] < requestedSharesCanonical) {
    i += 1
  }
  const prevCumAmount = i > 0 ? cumAmount[i - 1] : 0
  const prevCumShares = i > 0 ? cumShares[i - 1] : 0
  const prevCumFees = i > 0 ? cumFees[i - 1] : 0
  const { level, levelShares, levelTotalAmount, levelFees } = levels[i] 
  const sharesIntoLevel = requestedSharesCanonical - prevCumShares
  const shareFraction = sharesIntoLevel / levelShares
  const amountFromLevel = shareFraction * levelTotalAmount
  const feeFromLevel = levelFees * shareFraction

  return {
    grossAmountCanonical: prevCumAmount + amountFromLevel,
    totalFeeCanonical: prevCumFees + feeFromLevel,
    sharesConsumed: requestedSharesCanonical,
    remainingShares: 0,
    lastFilledLevel: level,
    fullyFilled: true,
  }
}

/**
 * @param {Array} series - Order book series (any sort order).
 * @param {number} requestedAmount - Amount to spend in canonical units.
 */
export function calculateFeesForAmount(series, requestedAmount) {
  const sim = simulateOrderBookBuyByAmount(series, requestedAmount)
  return {
    totalFee: sim.totalFeeCanonical,
    sharesConsumed: sim.grossSharesCanonical,
    amountConsumed: sim.amountConsumed,
  }
}

/**
 * Display-currency proceeds if the user sells `betShares` into the bid book (sellSeries).
 * Fees are assumed already reflected in the share amounts; proceeds use the same walk as
 * {@link simulateOrderBookSellByShares} in canonical space, then convert back for display.
 *
 * @param {object} p
 * @param {Array} p.sellSeries - bid side levels from the store
 * @param {number} p.betShares - display share count (e.g. bet.shares)
 * @param {{ display_ratio: number }} p.currency
 * @param {number} p.exchangeRateValue - same as convertToCanonical / convertFromCanonical (1 for OOM)
 * @returns {number | null} display-currency amount, or null if inputs are unusable
 */
export function computeNetAmountOnSellFromOrderBook({ sellSeries, betShares, currency, exchangeRateValue }) {
  if (!sellSeries?.length || !currency || !Number.isFinite(betShares) || betShares <= 0) {
    return null
  }

  const canonicalShares = convertToCanonical(betShares * currency.display_ratio, exchangeRateValue)
  const sim = simulateOrderBookSellByShares(sellSeries, canonicalShares)
  return convertFromCanonical(Math.max(0, sim.grossAmountCanonical), exchangeRateValue)
}

const MAKER_REBATE_PERCENTAGE = 0.8
const PLATFORM_FEE = 0.05

/**
 * Maker rebate on the unmatched portion of a limit order, in display currency.
 * Shares that rest in the book earn back a share of the platform fee, scaled by
 * price * (1 - price) so mid-book orders earn the most.
 *
 * @param {object} p
 * @param {number|string} p.shares - total shares on the order (display units)
 * @param {number|string} p.matchedShares - shares that fill immediately (display units)
 * @param {number|string} p.price - limit price
 * @returns {number} rebate rounded to 2 decimals, 0 when nothing rests in the book
 */
export function computeMakerRebate({ shares, matchedShares, price }) {
  const priceNum = parseFloat(price) || 0
  const makerShares = (parseFloat(shares) || 0) - Math.max(0, parseFloat(matchedShares) || 0)
  if (makerShares <= 0 || priceNum <= 0) return 0
  const rebate = PLATFORM_FEE * makerShares * priceNum * (1 - priceNum) * MAKER_REBATE_PERCENTAGE
  return parseFloat(rebate.toFixed(2))
}
